import React, { useRef } from 'react';
import { X } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from './pilot-ui/sheet';
import Button from './ui/Button';
import { resolveReturnFocus } from './returnFocus';

export interface SideDrawerProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose: () => void;
  closeLabel?: string;
  ariaLabel?: string;
  widthClass?: string;
  children: React.ReactNode;
}

const SideDrawer: React.FC<SideDrawerProps> = ({
  title,
  subtitle,
  onClose,
  closeLabel = 'Close',
  ariaLabel,
  widthClass = 'max-w-3xl',
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const initialFocus = () => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return panelRef.current;
  };
  const finalFocus = () => {
    return resolveReturnFocus(openerRef.current);
  };
  const close = () => {
    const modalLayers = Array.from(document.querySelectorAll<HTMLElement>('[aria-modal="true"]'));
    const topLayer = modalLayers.at(-1);
    if (topLayer && topLayer !== panelRef.current) return;
    onClose();
  };

  return (
    <Sheet open onOpenChange={(open) => { if (!open) close(); }}>
      <SheetContent
        ref={panelRef}
        side="right"
        tabIndex={-1}
        showCloseButton={false}
        customWidth
        className={`w-full ${widthClass} gap-0 overflow-y-auto`}
        aria-label={ariaLabel}
        initialFocus={initialFocus}
        finalFocus={finalFocus}
      >
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <SheetTitle className="text-lg font-semibold">{title}</SheetTitle>
            {subtitle && <SheetDescription className="mt-1">{subtitle}</SheetDescription>}
          </div>
          <Button variant="icon" size="md" onClick={close} aria-label={closeLabel} title={closeLabel}>
            <X data-icon-size="" className="h-5 w-5" />
          </Button>
        </div>
        {children}
      </SheetContent>
    </Sheet>
  );
};

export default SideDrawer;
